import { FormUsersSelect } from "@components/form/fields/FormUsersSelect";

import { OrgUnitDto, Tenant } from "@libs/api/gateways/plt/plt-gateway.dtos";
import { FunctionComponent } from "react";

import {
  Dialog,
  DropdownField,
  SubmissionForm,
  TextInputField,
  ToggleField,
  Option,
  Stack
} from "@bps/fluent-ui";
import { CallbackDto } from "@libs/api/gateways/plt-integrators/plt-integrators-gateway.dtos";
import {
  useCallbackTypes,
  useCreateCallback
} from "@libs/api/gateways/plt-integrators/plt-integrators-gateway.hooks";

export interface CreateCallbackDialogProps {
  tenant: Tenant;
  callbacks: CallbackDto[];
  orgUnits: OrgUnitDto[];
  onDismiss: () => void;
}

export interface CreateCallbackFormValues {
  id: string;
  key: string;
  type: string;
  isEnabled: boolean;
  tenantId: string;
  locationId: string;
  userId: string;
  propertyBag: string;
}

export const CreateCallbackDialog: FunctionComponent<CreateCallbackDialogProps> = ({
  tenant,
  orgUnits,
  onDismiss
}) => {
  const { mutateAsync: createCallback } = useCreateCallback();

  const callbackTypes = useCallbackTypes();

  const onSubmit = async (formData: CreateCallbackFormValues) => {
    await createCallback({
      tenantId: tenant.id,
      type: formData.type,
      key: formData.key,
      locationId: formData.locationId,
      userId: formData.userId,
      propertyBag: formData.propertyBag,
      isEnabled: formData.isEnabled
    });
    onDismiss();
  };

  const defaultValues = {
    type: "",
    key: "",
    locationId: "",
    userId: "",
    propertyBag: "",
    isEnabled: false,
    tenantId: tenant.id,
    id: ""
  };

  const callbackTypesOptions: Option[] = !callbackTypes
    ? []
    : [
        {
          key: "",
          text: "None"
        }
      ].concat(
        callbackTypes.data?.map(t => {
          return { key: t.code, text: t.code };
        }) ?? []
      );

  const locationOrgUniIdOptions: Option[] = !orgUnits
    ? []
    : [
        {
          key: "",
          text: "None"
        }
      ].concat(
        orgUnits?.map(t => {
          return { key: t.id, text: t.name };
        }) ?? []
      );

  return (
    <Dialog
      hidden={false}
      dialogContentProps={{
        title: "Configure Callback",
        showCloseButton: true,
        onDismiss
      }}
      minWidth={450}
    >
      <SubmissionForm<CreateCallbackFormValues>
        onSubmit={onSubmit}
        initialValues={defaultValues}
        hideCancelButton
      >
        {() => {
          return (
            <Stack tokens={{ childrenGap: 8 }}>
              <DropdownField
                name="type"
                placeholder="Select callback type"
                label="Callback Type"
                options={callbackTypesOptions}
                withNoEmptyOption={true}
              />
              <TextInputField
                label="Callback Key"
                name="key"
                placeholder="Callback Key"
              />
              <DropdownField
                name="locationId"
                placeholder="Select location org unit"
                label="Location OrgUnit"
                options={locationOrgUniIdOptions}
                withNoEmptyOption={true}
              />
              <FormUsersSelect
                label="User"
                name="userId"
                tenantId={tenant.id}
              />
              <TextInputField
                label="Property Bag Json Data"
                name="propertyBag"
                placeholder="Property bag json data"
                multiline
              />

              <ToggleField
                name="isEnabled"
                label="Enabled"
                styles={{
                  root: { margin: 0, flexGrow: 1 },
                  container: { marginTop: 4 }
                }}
              />
            </Stack>
          );
        }}
      </SubmissionForm>
    </Dialog>
  );
};
